import React, { useState, useEffect, Suspense, lazy } from 'react';
import { LayoutDashboard, BarChart2, Film, MapPin, ScanLine, Settings, MoreHorizontal, Table, Swords, Trophy, CalendarDays, Archive, Sparkles } from 'lucide-react';
import './index.css';
import Dashboard from './pages/Dashboard';
import ChartsPage from './pages/ChartsPage';
import ShowdownPage from './pages/ShowdownPage';
import RankingsPage from './pages/RankingsPage';
import PredictionsPage from './pages/PredictionsPage';
import EventsPage from './pages/EventsPage';
import ArchivePage from './pages/ArchivePage';
import MoviesPage from './pages/MoviesPage';
import CinemasPage, { defaultVenueView, venueRangeWithMode, venueViewFromRange } from './pages/CinemasPage';
import CinemaDetail from './pages/CinemaDetail';
import MovieDetail from './pages/MovieDetail';
import ScrapePage from './pages/ScrapePage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import UserManagementPage from './pages/UserManagementPage';
import RoleManagementPage from './pages/RoleManagementPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { checkHealth } from './api/client';
import useWindowWidth from './hooks/useWindowWidth';
import HeaderSearch from './components/HeaderSearch';
import WhatsNewModal from './components/WhatsNewModal';

const ScraperDebugPage = lazy(() => import('./pages/ScraperDebugPage'));

import {
  ACCENT, ACCENT_BG, BORDER_SUBTLE, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_FAINT,
  SIDEBAR_BG, SIDEBAR_BRD, HDR_BG, NEU_BG, NEU_RAISED, NEU_PRESSED, SURF_HOV,
} from './theme';
import packageJson from '../package.json';

const ALL_NAV_ITEMS = [
  { key: 'dashboard', permKey: 'dashboard.access', label: 'מה חדש', letter: 'מ', icon: LayoutDashboard },
  { key: 'charts',    permKey: 'charts.access',    label: 'טבלאות',  letter: 'ט', icon: Table },
  { key: 'rankings',  permKey: 'rankings.access',  label: 'דירוגים', letter: 'ד', icon: Trophy },
  { key: 'archive',   permKey: 'archive.access',   label: 'סופי שבוע',  letter: 'ש', icon: Archive },
  { key: 'events',    permKey: 'events.access',    label: 'אירועים', letter: 'א', icon: CalendarDays },
  { key: 'predictions', permKey: 'predictions.access', label: 'תחזיות', letter: 'ת', icon: Sparkles, experimental: true },
  { key: 'movies',    permKey: 'movies.access',    label: 'סרטים',   letter: 'ס', icon: Film },
  { key: 'cinemas',   permKey: 'cinemas.access',   label: 'סניפים',  letter: 'פ', icon: MapPin },
  { key: 'analytics', permKey: 'analytics.access', label: 'ניתוחים', letter: 'נ', icon: BarChart2 },
  { key: 'showdown',  permKey: 'showdown.access',  label: 'השוואה',  letter: 'ה', icon: Swords },
  { key: 'scrape',    permKey: 'scrape.access',    label: 'סריקה',   letter: 'ר', icon: ScanLine },
  { key: 'admin',     permKey: 'admin.access',     label: 'ניהול',   letter: 'נ', icon: Settings },
];

const PAGE_LABELS = {
  dashboard: 'מה חדש', analytics: 'ניתוחים', charts: 'טבלאות', showdown: 'השוואה', rankings: 'דירוגים', movies: 'סרטים',
  cinemas: 'סניפים', cinemaDetail: 'סניף', scrape: 'סריקה', admin: 'ניהול', events: 'אירועים', archive: 'סופי שבוע',
  movieDetail: 'פרטי סרט', users: 'משתמשים', 'user-roles': 'סוגי משתמשים',
  predictions: 'תחזיות (ניסיוני)',
};

const APP_VERSION = process.env.REACT_APP_VERSION || packageJson.version;


function AppInner() {
  const { user, loading, logout, hasPermission } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [selectedMovieId, setSelectedMovieId] = useState(null);
  const [showdownSeed, setShowdownSeed] = useState(null);
  // The charts page's period (day / weekend / week / custom range). "מה חדש"
  // and "סופי שבוע" open a given day or weekend through it, and it outlives a
  // visit to a film, so "back" lands on the same table.
  const [chartsView, setChartsView] = useState(null);
  // Branches page state (period, chips, drill-down) outlives a visit to a
  // branch, so "back" lands where the user left and both pages share one range.
  const [venueView, setVenueView] = useState(null);
  // The rankings page's period and category, and the weekends page's year, kept the same way.
  const [rankView, setRankView] = useState(null);
  const [archiveYear, setArchiveYear] = useState(null);
  const [selectedCinemaId, setSelectedCinemaId] = useState(null);
  const [backendStatus, setBackendStatus] = useState('checking');
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('kup-dark') === '1'; } catch { return false; }
  });
  const width = useWindowWidth();
  const mob = width < 768;
  const col = !mob && width < 1120;

  useEffect(() => {
    document.documentElement.dataset.theme = darkMode ? 'dark' : 'light';
    try { localStorage.setItem('kup-dark', darkMode ? '1' : '0'); } catch {}
  }, [darkMode]);

  useEffect(() => {
    const check = () => checkHealth().then(({ ok }) => setBackendStatus(ok ? 'online' : 'offline'));
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, []);

  // Navigate to first permitted page when user logs in
  useEffect(() => {
    if (user) {
      const navItem = ALL_NAV_ITEMS.find(n => n.key === page);
      const permKey = navItem?.permKey;
      if (permKey && !hasPermission(permKey) && !['movieDetail', 'cinemaDetail', 'users', 'user-roles'].includes(page) && !page.startsWith('debug-')) {
        const first = ALL_NAV_ITEMS.find(n => hasPermission(n.permKey));
        if (first) setPage(first.key);
      }
    }
  }, [user]); // eslint-disable-line

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', fontFamily: 'Heebo, sans-serif', color: TEXT_FAINT,
      }}>
        טוען...
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const NAV_ITEMS = ALL_NAV_ITEMS.filter(n => hasPermission(n.permKey));
  const NAV_MOBILE_PRIMARY = NAV_ITEMS.slice(0, 3);
  const NAV_MOBILE_MORE = NAV_ITEMS.slice(3);

  const navigateToMovie = (id) => { setSelectedMovieId(id); setPage('movieDetail'); };
  const navigateToCinema = (id) => { setSelectedCinemaId(id); setPage('cinemaDetail'); };
  const venue = venueView || defaultVenueView();
  const navigateToShowdown = (id) => { setShowdownSeed(id || null); setPage('showdown'); };
  // Deep links: navigate('charts', {mode,start,end}) opens the charts on that range
  const navigateWithSeed = (key, seed) => { if (key === 'charts' && seed) setChartsView(seed); setPage(key); };

  const renderPage = () => {
    // Per-chain scraper debug pages require their own permission
    if (page.startsWith('debug-') && !hasPermission('debug.chain_pages')) {
      return (
        <div style={{ textAlign: 'center', padding: 60, color: TEXT_FAINT, fontFamily: 'Heebo, sans-serif' }}>
          אין לך הרשאה לצפות בדפי הדיבאג
        </div>
      );
    }
    switch (page) {
      case 'dashboard':   return <Dashboard onMovieClick={navigateToMovie} onNavigate={navigateWithSeed} refreshNonce={refreshNonce} />;
      case 'movies':      return <MoviesPage onMovieClick={navigateToMovie} refreshNonce={refreshNonce} />;
      case 'cinemas':     return <CinemasPage refreshNonce={refreshNonce} view={venue} onViewChange={setVenueView} onCinemaClick={navigateToCinema} onMovieClick={navigateToMovie} />;
      case 'cinemaDetail': return <CinemaDetail cinemaId={selectedCinemaId} range={venueRangeWithMode(venue)} onRangeChange={(r) => setVenueView(venueViewFromRange(venue, r))} onBack={() => setPage('cinemas')} onMovieClick={navigateToMovie} refreshNonce={refreshNonce} />;
      case 'analytics':   return <AnalyticsPage refreshNonce={refreshNonce} />;
      case 'charts':      return <ChartsPage onMovieClick={navigateToMovie} refreshNonce={refreshNonce} view={chartsView} onViewChange={setChartsView} />;
      case 'showdown':    return <ShowdownPage seedMovieId={showdownSeed} refreshNonce={refreshNonce} />;
      case 'rankings':    return <RankingsPage onMovieClick={navigateToMovie} refreshNonce={refreshNonce} view={rankView} onViewChange={setRankView} />;
      case 'events':      return <EventsPage onMovieClick={navigateToMovie} refreshNonce={refreshNonce} />;
      case 'predictions': return <PredictionsPage onMovieClick={navigateToMovie} refreshNonce={refreshNonce} />;
      case 'archive':     return <ArchivePage onMovieClick={navigateToMovie} onNavigate={navigateWithSeed} refreshNonce={refreshNonce} year={archiveYear} onYearChange={setArchiveYear} />;
      case 'scrape':      return <ScrapePage refreshNonce={refreshNonce} />;
      case 'admin':       return <AdminPage refreshNonce={refreshNonce} onNavigate={setPage} />;
      case 'users':       return <UserManagementPage />;
      case 'user-roles':  return <RoleManagementPage />;
      case 'movieDetail': return <MovieDetail movieId={selectedMovieId} onBack={() => setPage('movies')} onShowdown={navigateToShowdown} refreshNonce={refreshNonce} />;
      case 'debug-hot':   return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="hot_cinema" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-mvl':   return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="movieland" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-cc':    return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="cinema_city" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-lev':   return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="lev" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-planet':return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="planet" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-rav_hen':          return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="rav_hen" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-cinematheque_tlv': return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="cinematheque_tlv" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-cinematheque_jlm':      return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="cinematheque_jlm" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-cinematheque_herzliya': return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="cinematheque_herzliya" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-cinematheque_holon':    return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="cinematheque_holon"    refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-cinematheque_haifa':    return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="cinematheque_haifa"    refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-cinematheque_rosh_pina': return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="cinematheque_rosh_pina" refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      case 'debug-cinematheque_sderot':   return <Suspense fallback={<div style={{padding:40}}>טוען...</div>}><ScraperDebugPage chainKey="cinematheque_sderot"   refreshNonce={refreshNonce} onBack={() => setPage('admin')} /></Suspense>;
      default:            return <Dashboard onMovieClick={navigateToMovie} refreshNonce={refreshNonce} />;
    }
  };

  const activeNav = page === 'movieDetail' ? 'movies'
    : page.startsWith('debug-') ? 'admin'
    : ['users', 'user-roles'].includes(page) ? 'admin'
    : page;
  const pageLabel = PAGE_LABELS[page] || 'ZivlinBoxOffice';

  const statusColor = backendStatus === 'online' ? 'var(--ok)' : backendStatus === 'offline' ? 'var(--danger)' : 'var(--warn)';
  const statusLabel = backendStatus === 'online' ? 'שרת מחובר' : backendStatus === 'offline' ? 'שרת מנותק' : 'בודק...';

  // ── Mobile layout ─────────────────────────────────────────────────────────
  if (mob) {
    return (
      <div style={{ minHeight: '100vh', paddingBottom: 88 }}>
        <WhatsNewModal user={user} />
        <header style={{
          position: 'sticky', top: 0, zIndex: 200,
          background: HDR_BG, backdropFilter: 'blur(18px)',
          borderBottom: `1px solid ${BORDER_SUBTLE}`,
          height: 52, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '0 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>🎬</div>
            <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.5 }}>
              <span style={{ color: ACCENT }}>Zivlin</span>
              <span style={{ color: TEXT_PRIMARY }}>BoxOffice</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {hasPermission('movie_detail.access') && <HeaderSearch onMovieClick={navigateToMovie} mobile />}
            <span style={{ fontSize: 12, color: TEXT_FAINT }}>{user.username}</span>
            <button onClick={logout} title="התנתק" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: TEXT_SECONDARY }}>⎋</button>
            <button onClick={() => setDarkMode(d => !d)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: TEXT_SECONDARY }}>
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={() => setRefreshNonce(v => v + 1)} style={{ background: 'none', border: 'none', color: TEXT_SECONDARY, fontSize: 16, cursor: 'pointer' }}>↻</button>
          </div>
        </header>

        <main style={{ padding: '14px 16px', maxWidth: '100vw', overflowX: 'hidden' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 12 }}>{pageLabel}</div>
          <div key={page} style={{ animation: 'fadeUp .22s ease' }}>{renderPage()}</div>
        </main>

        {moreOpen && (
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.4)' }}
              onClick={() => setMoreOpen(false)}
            />
            <div style={{
              position: 'fixed', bottom: 70, left: 0, right: 0, zIndex: 301,
              background: HDR_BG, backdropFilter: 'blur(20px)',
              borderTop: `1px solid ${BORDER_SUBTLE}`,
              borderRadius: '16px 16px 0 0',
              padding: '16px 0 8px',
            }}>
              {NAV_MOBILE_MORE.map(n => {
                const on = activeNav === n.key;
                return (
                  <button key={n.key} onClick={() => { setPage(n.key); setMoreOpen(false); }} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    width: '100%', padding: '13px 24px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: 'Heebo, sans-serif', textAlign: 'right',
                    borderBottom: `1px solid ${BORDER_SUBTLE}`,
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: on ? ACCENT : 'transparent', flexShrink: 0 }} />
                    <span style={{ fontSize: 15, fontWeight: on ? 700 : 400, color: on ? ACCENT : TEXT_PRIMARY }}>
                      {n.label}
                      {n.experimental && (
                        <span style={{
                          marginRight: 6, fontSize: 9, fontWeight: 700, padding: '1px 5px',
                          borderRadius: 5, background: 'var(--warn)', color: '#fff',
                          verticalAlign: 'middle',
                        }}>ניסיוני</span>
                      )}
                    </span>
                  </button>
                );
              })}
              <button onClick={() => { logout(); setMoreOpen(false); }} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                width: '100%', padding: '13px 24px',
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'Heebo, sans-serif', textAlign: 'right',
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'transparent', flexShrink: 0 }} />
                <span style={{ fontSize: 15, color: 'var(--danger, #ef4444)' }}>התנתק ({user.username})</span>
              </button>
            </div>
          </>
        )}

        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
          background: HDR_BG, backdropFilter: 'blur(20px)',
          borderTop: `1px solid ${BORDER_SUBTLE}`,
          display: 'flex', justifyContent: 'space-around',
          paddingTop: 10, paddingBottom: 24,
        }}>
          {NAV_MOBILE_PRIMARY.map(n => {
            const on = activeNav === n.key;
            const Icon = n.icon;
            return (
              <button key={n.key} onClick={() => { setPage(n.key); setMoreOpen(false); }} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', cursor: 'pointer', flex: 1,
                fontFamily: 'Heebo, sans-serif',
              }}>
                <Icon size={22} strokeWidth={on ? 2.2 : 1.6} color={on ? ACCENT : TEXT_FAINT} style={{ transition: 'color .2s' }} />
                <span style={{ fontSize: 11, fontWeight: on ? 700 : 400, color: on ? ACCENT : TEXT_FAINT, transition: 'color .2s' }}>{n.label}</span>
              </button>
            );
          })}
          {NAV_MOBILE_MORE.length > 0 && (() => {
            const moreOn = moreOpen || NAV_MOBILE_MORE.some(n => n.key === activeNav);
            return (
              <button onClick={() => setMoreOpen(o => !o)} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', cursor: 'pointer', flex: 1,
                fontFamily: 'Heebo, sans-serif',
              }}>
                <MoreHorizontal size={22} strokeWidth={moreOn ? 2.2 : 1.6} color={moreOn ? ACCENT : TEXT_FAINT} style={{ transition: 'color .2s' }} />
                <span style={{ fontSize: 11, fontWeight: moreOn ? 700 : 400, color: moreOn ? ACCENT : TEXT_FAINT, transition: 'color .2s' }}>עוד</span>
              </button>
            );
          })()}
        </nav>
      </div>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <WhatsNewModal user={user} />
      {/* Sidebar */}
      <aside style={{
        width: col ? 64 : 220,
        flexShrink: 0,
        background: SIDEBAR_BG,
        borderLeft: `1px solid ${SIDEBAR_BRD}`,
        display: 'flex', flexDirection: 'column',
        height: '100vh', position: 'sticky', top: 0,
        padding: col ? '20px 10px' : '20px 14px',
        overflow: 'hidden', transition: 'width .3s',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, justifyContent: col ? 'center' : 'flex-start' }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>🎬</div>
          {!col && (
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1 }}>
                <span style={{ color: ACCENT }}>Zivlin</span>
                <span style={{ color: TEXT_PRIMARY }}>BoxOffice</span>
              </div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav style={{
          display: 'flex', flexDirection: 'column', gap: 2,
          flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'thin',
        }}>
          {NAV_ITEMS.map(n => {
            const on = activeNav === n.key;
            return (
              <button key={n.key} onClick={() => setPage(n.key)} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: col ? '7px' : '6px 10px', borderRadius: 9, border: 'none',
                width: '100%', cursor: 'pointer', justifyContent: col ? 'center' : 'flex-start',
                background: on ? ACCENT_BG : 'transparent',
                color: on ? ACCENT : TEXT_SECONDARY,
                fontFamily: 'Heebo, sans-serif', fontSize: 13,
                fontWeight: on ? 600 : 400, transition: 'all .15s',
              }}
              onMouseEnter={e => { if (!on) e.currentTarget.style.background = SURF_HOV; }}
              onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{
                  width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                  background: on ? ACCENT : 'var(--surf-alt)',
                  color: on ? '#fff' : TEXT_FAINT,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, transition: 'all .15s',
                }}>{n.letter}</div>
                {!col && (
                  <span>
                    {n.label}
                    {n.experimental && (
                      <span style={{
                        marginRight: 6, fontSize: 9, fontWeight: 700, padding: '1px 5px',
                        borderRadius: 5, background: 'var(--warn)', color: '#fff',
                        verticalAlign: 'middle',
                      }}>ניסיוני</span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User info + logout + status + version */}
        <div style={{ paddingTop: 14, borderTop: `1px solid ${BORDER_SUBTLE}` }}>
          {!col && (
            <>
              {/* User chip */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%',
                    background: user.is_admin ? ACCENT : 'var(--surf-alt)',
                    color: user.is_admin ? '#fff' : TEXT_FAINT,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                  }}>
                    {user.username[0].toUpperCase()}
                  </div>
                  <span style={{ fontSize: 12, color: TEXT_PRIMARY, fontWeight: 600 }}>{user.username}</span>
                </div>
                <button
                  onClick={logout}
                  title="התנתק"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, color: TEXT_FAINT, padding: '2px 4px', borderRadius: 4,
                  }}
                >⎋</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: TEXT_FAINT, marginBottom: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, display: 'inline-block', animation: backendStatus !== 'online' ? 'pulse 1.5s infinite' : 'none' }} />
                {statusLabel}
              </div>
              <div style={{ fontSize: 10, color: TEXT_FAINT }}>v{APP_VERSION} · נבנה על ידי קסם ברקוביץ</div>
            </>
          )}
          {col && (
            <button
              onClick={logout}
              title="התנתק"
              style={{
                width: '100%', padding: '9px', borderRadius: 10, border: 'none',
                background: 'transparent', cursor: 'pointer', color: TEXT_FAINT, fontSize: 16,
              }}
            >⎋</button>
          )}
        </div>
      </aside>

      {/* Content area */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 100,
          background: HDR_BG, backdropFilter: 'blur(18px)',
          borderBottom: `1px solid ${BORDER_SUBTLE}`,
          height: 54, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '0 28px',
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: TEXT_PRIMARY }}>{pageLabel}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {hasPermission('movie_detail.access') && <HeaderSearch onMovieClick={navigateToMovie} />}
            <button
              onClick={() => setDarkMode(d => !d)}
              title={darkMode ? 'מצב בהיר' : 'מצב לילה'}
              style={{
                padding: '6px 12px', borderRadius: 8, border: `1px solid ${BORDER_SUBTLE}`,
                background: NEU_BG, color: TEXT_SECONDARY, cursor: 'pointer',
                fontSize: 14, fontFamily: 'Heebo, sans-serif', transition: 'all .15s',
              }}
            >
              {darkMode ? '☀️ בהיר' : '🌙 לילה'}
            </button>
            <button
              onClick={() => setRefreshNonce(v => v + 1)}
              title="רענון נתונים"
              style={{
                padding: '6px 14px', borderRadius: 8, border: `1px solid ${BORDER_SUBTLE}`,
                background: NEU_BG, color: TEXT_SECONDARY, cursor: 'pointer',
                fontSize: 12, fontWeight: 500, fontFamily: 'Heebo, sans-serif', transition: 'all .15s',
              }}
            >
              ↻ רענן
            </button>
          </div>
        </div>

        {/* Page content */}
        <main style={{ padding: '22px 28px', width: '100%', boxSizing: 'border-box' }}>
          <div key={page} style={{ animation: 'fadeUp .22s ease' }}>
            {renderPage()}
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

export default App;
